import {
  Activity,
  AlertCircle,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  ExternalLink,
  Loader2,
  MessageSquare,
  Pencil,
  PlayCircle,
  Plus,
  RefreshCw,
  Settings2,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/catalyst/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  type CmsFeature,
  type CmsFeatureConfigSchema,
  type CmsFeatureSyncRunStatusSchema,
  type ExternalCmsType,
  type ScheduleSpec,
  useFirmsCmsFeatureConfigsCreateMutation,
  useFirmsCmsFeatureConfigsDestroyMutation,
  useFirmsCmsFeatureConfigsDetailsQuery,
  useFirmsCmsFeatureConfigsListQuery,
  useFirmsCmsFeatureConfigsPartialUpdateMutation,
  useFirmsCmsFeatureConfigsSupportedFeaturesQuery,
  useFirmsCmsFeatureConfigsTriggerMutation,
} from '@/services/api/generated/cmsFeatureConfigService';
import {
  buildTemporalScheduleUrl,
  buildTemporalWorkflowUrl,
} from '@/utils/temporalUtils';

// ---------------------------------------------------------------------------
// Display constants
// ---------------------------------------------------------------------------

// Extend the generated ExternalCmsType to include google_drive until API types are regenerated
type ExtendedCmsType = ExternalCmsType | 'google_drive';

const CMS_TYPE_DISPLAY: Record<
  ExtendedCmsType,
  { name: string; description: string }
> = {
  filevine: {
    name: 'Filevine',
    description: 'Legal project management platform',
  },
  clio: { name: 'Clio', description: 'Cloud-based legal practice management' },
  dropbox: {
    name: 'Dropbox',
    description: 'Cloud file storage and document sync',
  },
  google_drive: {
    name: 'Google Drive',
    description: 'Cloud document storage and sync',
  },
  neos: { name: 'NEOS', description: 'Case management software' },
  litify: {
    name: 'Litify',
    description: 'Legal operations platform on Salesforce',
  },
  casepeer: {
    name: 'CASEpeer',
    description: 'Personal injury case management',
  },
  mycase: { name: 'MyCase', description: 'Legal practice management software' },
  smartadvocate: {
    name: 'SmartAdvocate',
    description: 'Case management system',
  },
};

const FEATURE_DISPLAY: Record<
  CmsFeature,
  { name: string; description: string }
> = {
  task_sync: {
    name: 'Task Sync',
    description: 'Synchronize tasks between systems',
  },
  document_source: {
    name: 'Document Source',
    description: 'Pull documents from the CMS',
  },
  contact_sync: {
    name: 'Contact Sync',
    description: 'Synchronize contacts between systems',
  },
};

// ---------------------------------------------------------------------------
// Schedule presets & helpers
// ---------------------------------------------------------------------------

interface SchedulePreset {
  label: string;
  shortLabel: string;
  description: string;
  spec: ScheduleSpec;
}

const SCHEDULE_PRESETS: SchedulePreset[] = [
  {
    label: 'Every 2 hours',
    shortLabel: 'Every 2 hrs',
    description: 'Frequent syncing throughout the day',
    spec: { intervals: [{ every: 'PT2H' }] },
  },
  {
    label: 'Every 4 hours',
    shortLabel: 'Every 4 hrs',
    description: 'Good balance of freshness and efficiency',
    spec: { intervals: [{ every: 'PT4H' }] },
  },
  {
    label: 'Every 6 hours',
    shortLabel: 'Every 6 hrs',
    description: 'Syncs 4 times per day',
    spec: { intervals: [{ every: 'PT6H' }] },
  },
  {
    label: 'Every 12 hours',
    shortLabel: 'Twice daily',
    description: 'Morning and evening sync',
    spec: { intervals: [{ every: 'PT12H' }] },
  },
  {
    label: 'Once a day (7 AM EST)',
    shortLabel: 'Daily 7 AM',
    description: 'Runs once each morning before business hours',
    spec: {
      cron_expressions: ['0 7 * * *'],
      time_zone_name: 'America/New_York',
    },
  },
  {
    label: 'Twice a day (7 AM & 12 PM EST)',
    shortLabel: 'Daily 7 AM & 12 PM',
    description: 'Morning and midday refresh',
    spec: {
      cron_expressions: ['0 7,12 * * *'],
      time_zone_name: 'America/New_York',
    },
  },
];

function parseDurationHours(iso: string): number {
  const match = iso.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!match) return 0;
  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  return hours + minutes / 60;
}

function formatTimeOfDay(hour24: number, minute: number): string {
  const base = formatHour(hour24 % 24);
  if (minute === 0) return base;
  const minStr = minute.toString().padStart(2, '0');
  return base.replace(/ (AM|PM)$/, `:${minStr} $1`);
}

function timeStrToMinutes(timeStr: string): number {
  const [hh, mm] = timeStr.split(':');
  const h = parseInt(hh ?? '0', 10);
  const m = parseInt(mm ?? '0', 10);
  return h * 60 + m;
}

function canonicalizeDailyCronExpressions(
  crons: string[],
): { ok: true; times: string[] } | { ok: false } {
  const times: string[] = [];

  const ok = crons.every((cron) => {
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return false;
    const [min, hour, dom, mon, dow] = parts;
    if (dom !== '*' || mon !== '*' || dow !== '*') return false;
    if (!/^\d+$/.test(min)) return false;
    const minute = parseInt(min, 10);
    if (minute < 0 || minute > 59) return false;

    return hour.split(',').every((hStr) => {
      if (!/^\d+$/.test(hStr)) return false;
      const hour24 = parseInt(hStr, 10);
      if (hour24 < 0 || hour24 > 23) return false;
      times.push(
        `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
      );
      return true;
    });
  });

  if (!ok) return { ok: false };

  const uniqueSorted = Array.from(new Set(times)).sort(
    (a, b) => timeStrToMinutes(a) - timeStrToMinutes(b),
  );
  return { ok: true, times: uniqueSorted };
}

function describeTimes(times: string[], tz: string): string {
  const fmt = times.map((t) => {
    const [hh, mm] = t.split(':');
    return formatTimeOfDay(
      parseInt(hh ?? '0', 10),
      parseInt(mm ?? '0', 10),
    );
  });

  const tzLabel = formatTimezone(tz);
  if (fmt.length === 0) return `Custom (${tzLabel})`;
  if (fmt.length === 1) return `Daily ${fmt[0]} ${tzLabel}`;
  if (fmt.length === 2) return `${fmt[0]} & ${fmt[1]} ${tzLabel}`;
  if (fmt.length === 3) return `${fmt[0]}, ${fmt[1]} & ${fmt[2]} ${tzLabel}`;
  return `${fmt[0]}, ${fmt[1]} +${fmt.length - 2} more ${tzLabel}`;
}

interface OffsetOption {
  label: string;
  value: string | null;
  description: string;
}

function buildOffsetOptions(intervalEvery: string): OffsetOption[] {
  const maxHours = parseDurationHours(intervalEvery);
  const options: OffsetOption[] = [
    {
      label: 'No preference',
      value: null,
      description: 'Sync starts from when the schedule is set',
    },
  ];
  for (let h = 0; h < 24 && h < maxHours; h += 1) {
    options.push({
      label: formatHour(h),
      value: `PT${h}H`,
      description: h === 0 ? 'Midnight' : formatHour(h),
    });
  }
  return options;
}

function formatDuration(iso: string): string {
  const match = iso.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!match) return iso;
  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.length > 0 ? parts.join(' ') : iso;
}

function describeSchedule(spec: ScheduleSpec | null | undefined): string {
  if (!spec) return 'No schedule';

  if (spec.intervals && spec.intervals.length > 0) {
    const interval = spec.intervals[0];
    let label = `Every ${formatDuration(interval.every)}`;
    if (interval.offset) {
      label += ` from ${formatOffsetAsTime(interval.offset)}`;
    }
    return label;
  }

  if (spec.cron_expressions && spec.cron_expressions.length > 0) {
    const tz = spec.time_zone_name ?? 'UTC';
    const normalized = canonicalizeDailyCronExpressions(spec.cron_expressions);
    if (normalized.ok) return describeTimes(normalized.times, tz);

    const cron = spec.cron_expressions[0];
    return `${cron} (${formatTimezone(tz)})`;
  }

  return 'Custom';
}

function formatHour(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour > 12) return `${hour - 12} PM`;
  return `${hour} AM`;
}

function formatTimezone(tz: string): string {
  if (tz === 'America/New_York') return 'EST';
  if (tz === 'America/Chicago') return 'CST';
  if (tz === 'America/Denver') return 'MST';
  if (tz === 'America/Los_Angeles') return 'PST';
  if (tz === 'UTC') return 'UTC';
  return tz;
}

function formatOffsetAsTime(iso: string): string {
  const match = iso.match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/);
  if (!match) return iso;
  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const minStr = minutes.toString().padStart(2, '0');
  return `${formatHour(hours % 24).replace(/ (AM|PM)/, `:${minStr} $1`)}`;
}

function specsMatch(
  a: ScheduleSpec | null | undefined,
  b: ScheduleSpec,
): boolean {
  if (!a) return false;
  if (b.intervals && b.intervals.length > 0) {
    if (!a.intervals || a.intervals.length === 0) return false;
    return a.intervals[0].every === b.intervals[0].every;
  }
  if (b.cron_expressions && b.cron_expressions.length > 0) {
    if (!a.cron_expressions || a.cron_expressions.length === 0) return false;
    const tzA = a.time_zone_name ?? 'UTC';
    const tzB = b.time_zone_name ?? 'UTC';
    if (tzA !== tzB) return false;

    const normA = canonicalizeDailyCronExpressions(a.cron_expressions);
    const normB = canonicalizeDailyCronExpressions(b.cron_expressions);
    if (normA.ok && normB.ok) {
      return normA.times.join('|') === normB.times.join('|');
    }
    return a.cron_expressions[0] === b.cron_expressions[0];
  }
  return false;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function normalizeWorkflowStatus(status: string | null | undefined): string {
  if (!status) return 'Unknown';
  const normalized = status.replace('WorkflowExecutionStatus.', '');
  const lower = normalized.toLowerCase();
  if (lower === 'completed') return 'Completed';
  if (lower === 'running') return 'Running';
  if (lower === 'failed') return 'Failed';
  if (lower === 'terminated') return 'Terminated';
  if (lower === 'timed_out') return 'Timed out';
  if (lower === 'continued_as_new') return 'Continued';
  if (lower === 'canceled') return 'Canceled';
  return normalized;
}

function statusToneClass(status: string | null | undefined): string {
  const normalized = normalizeWorkflowStatus(status).toLowerCase();
  if (normalized === 'completed') {
    return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  }
  if (normalized === 'running') {
    return 'bg-blue-50 text-blue-700 border border-blue-200';
  }
  if (
    normalized === 'failed' ||
    normalized === 'timed out' ||
    normalized === 'terminated'
  ) {
    return 'bg-rose-50 text-rose-700 border border-rose-200';
  }
  return 'bg-gray-50 text-gray-600 border border-gray-200';
}

function hasScheduleSpec(spec: ScheduleSpec | null | undefined): boolean {
  if (!spec) return false;
  return (
    (!!spec.intervals && spec.intervals.length > 0) ||
    (!!spec.cron_expressions && spec.cron_expressions.length > 0)
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface CmsFeatureConfigManagerProps {
  firmId: string;
}

export const CmsFeatureConfigManager: React.FC<
  CmsFeatureConfigManagerProps
> = ({ firmId }) => {
  const {
    data: configs = [],
    isLoading: isLoadingConfigs,
    refetch,
  } = useFirmsCmsFeatureConfigsListQuery({ firmId });

  const { data: supportedData, isLoading: isLoadingSupported } =
    useFirmsCmsFeatureConfigsSupportedFeaturesQuery({ firmId });

  const [createConfig] = useFirmsCmsFeatureConfigsCreateMutation();
  const [updateConfig] = useFirmsCmsFeatureConfigsPartialUpdateMutation();
  const [deleteConfig] = useFirmsCmsFeatureConfigsDestroyMutation();
  const [triggerConfig, { isLoading: isTriggering }] =
    useFirmsCmsFeatureConfigsTriggerMutation();

  // Track which row has its note editor expanded
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const [expandedHistoryConfigId, setExpandedHistoryConfigId] = useState<
    string | null
  >(null);

  const {
    data: detailsData,
    isFetching: isFetchingDetails,
    refetch: refetchDetails,
  } = useFirmsCmsFeatureConfigsDetailsQuery(
    {
      firmId,
      id: expandedHistoryConfigId ?? '',
      pk: expandedHistoryConfigId ?? '',
      runLimit: 12,
    },
    { skip: expandedHistoryConfigId === null },
  );

  const supportedFeatures = supportedData?.supported_features ?? {};
  const isLoading = isLoadingConfigs || isLoadingSupported;

  const configsByCmsType = useMemo(
    () =>
      configs.reduce<Record<string, CmsFeatureConfigSchema[]>>(
        (acc, config) => {
          const key = config.cms_type;
          if (!acc[key]) acc[key] = [];
          acc[key].push(config);
          return acc;
        },
        {},
      ),
    [configs],
  );

  const cmsTypes = Object.keys(supportedFeatures) as ExtendedCmsType[];

  // Handlers -----------------------------------------------------------

  const handleToggleEnabled = useCallback(
    async (configId: string, enabled: boolean) => {
      try {
        await updateConfig({
          firmId,
          id: configId,
          pk: configId,
          patchedUpdateCmsFeatureConfigSchema: { enabled },
        }).unwrap();
        toast.success(`Feature ${enabled ? 'enabled' : 'disabled'}`);
        refetch();
      } catch {
        toast.error('Failed to update feature');
      }
    },
    [firmId, updateConfig, refetch],
  );

  const handleSaveNote = useCallback(
    async (configId: string, description: string) => {
      try {
        await updateConfig({
          firmId,
          id: configId,
          pk: configId,
          patchedUpdateCmsFeatureConfigSchema: { description },
        }).unwrap();
        toast.success('Note saved');
        setEditingNoteId(null);
        refetch();
      } catch {
        toast.error('Failed to save note');
      }
    },
    [firmId, updateConfig, refetch],
  );

  const handleSaveSchedule = useCallback(
    async (configId: string, schedule_spec: ScheduleSpec) => {
      try {
        await updateConfig({
          firmId,
          id: configId,
          pk: configId,
          patchedUpdateCmsFeatureConfigSchema: { schedule_spec },
        }).unwrap();
        toast.success('Schedule updated');
        refetch();
      } catch {
        toast.error('Failed to update schedule');
      }
    },
    [firmId, updateConfig, refetch],
  );

  const handleDelete = useCallback(
    async (configId: string) => {
      if (!window.confirm('Delete this feature configuration?')) return;
      try {
        await deleteConfig({ firmId, id: configId, pk: configId }).unwrap();
        toast.success('Feature deleted');
        refetch();
      } catch {
        toast.error('Failed to delete');
      }
    },
    [firmId, deleteConfig, refetch],
  );

  const handleTrigger = useCallback(
    async (configId: string) => {
      try {
        await triggerConfig({ firmId, id: configId, pk: configId }).unwrap();
        toast.success('Sync triggered');
        refetch();
      } catch {
        toast.error('Failed to trigger sync');
      }
    },
    [firmId, triggerConfig, refetch],
  );

  const handleCreateFeature = useCallback(
    async (cmsType: ExtendedCmsType, feature: CmsFeature) => {
      try {
        const siblings = configsByCmsType[cmsType] ?? [];
        const siblingWithSchedule = siblings.find((c) =>
          hasScheduleSpec(c.schedule_spec),
        );

        await createConfig({
          firmId,
          createCmsFeatureConfigSchema: {
            firm_id: firmId,
            cms_type: cmsType as ExternalCmsType,
            feature,
            enabled: false,
            ...(siblingWithSchedule
              ? { schedule_spec: siblingWithSchedule.schedule_spec }
              : {}),
          },
        }).unwrap();
        toast.success(`${FEATURE_DISPLAY[feature].name} added`);
        refetch();
      } catch {
        toast.error('Failed to create feature');
      }
    },
    [firmId, createConfig, refetch, configsByCmsType],
  );

  const startEditingNote = (config: CmsFeatureConfigSchema) => {
    setEditingNoteId(config.id);
    setNoteValue(config.description);
  };

  const toggleHistory = (configId: string) => {
    setExpandedHistoryConfigId((current) =>
      current === configId ? null : configId,
    );
  };

  // Render -------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-sm text-gray-500">Loading CMS features...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-50">
          <Settings2 className="h-5 w-5 text-violet-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            CMS Features
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure automatic sync schedules for each CMS integration.
          </p>
        </div>
      </div>

      {cmsTypes.length === 0 ? (
        <div className="border rounded-lg bg-white overflow-hidden">
          <div className="py-12 text-center text-gray-500">
            <RefreshCw className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-sm">
              No CMS integrations available
            </p>
            <p className="text-xs mt-1 text-gray-500">
              Features will appear once integrations are configured.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {cmsTypes.map((cmsType) => {
            const display = CMS_TYPE_DISPLAY[cmsType] ?? {
              name: cmsType,
              description: '',
            };
            const features = supportedFeatures[cmsType] ?? [];
            const existingConfigs = configsByCmsType[cmsType] ?? [];
            const configuredFeatures = new Set(
              existingConfigs.map((c) => c.feature),
            );
            const addableFeatures = features.filter(
              (f) => !configuredFeatures.has(f),
            );
            const enabledCount = existingConfigs.filter(
              (c) => c.enabled,
            ).length;

            return (
              <div
                key={cmsType}
                className="border rounded-lg bg-white overflow-hidden"
              >
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-50 border border-gray-100">
                      <span className="text-sm font-bold text-gray-600">
                        {display.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {display.name}
                        </h3>
                        <Badge
                          variant="outline"
                          className="text-[10px] font-medium px-1.5 py-0 h-5"
                        >
                          {enabledCount} / {features.length}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {display.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature rows */}
                {existingConfigs.length > 0 && (
                  <div className="border-t">
                    {/* Column header */}
                    <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 px-5 py-2 bg-gray-50/80 text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                      <span>Feature</span>
                      <span className="w-[180px]">Schedule</span>
                      <span className="w-[80px] text-center">Status</span>
                      <span className="w-[120px]" />
                    </div>

                    {existingConfigs.map((config) => {
                      const featureDisplay = FEATURE_DISPLAY[
                        config.feature
                      ] ?? {
                        name: config.feature,
                        description: '',
                      };
                      const isEditingNote = editingNoteId === config.id;
                      const schedule = describeSchedule(config.schedule_spec);
                      const scheduled = hasScheduleSpec(config.schedule_spec);

                      return (
                        <div
                          key={config.id}
                          className="border-t first:border-t-0"
                        >
                          {/* Main row */}
                          <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 px-5 py-3 group">
                            {/* Feature name + sync status */}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium truncate">
                                  {featureDisplay.name}
                                </span>
                                <SyncStatusBadge config={config} />
                                {config.description && !isEditingNote && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="text-gray-500 hover:text-gray-700 transition-colors"
                                        onClick={() =>
                                          startEditingNote(config)
                                        }
                                      >
                                        <MessageSquare className="h-3.5 w-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs max-w-xs">
                                        {config.description}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              {config.last_sync_error && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <AlertCircle className="h-3 w-3 text-red-500 shrink-0" />
                                  <span className="text-xs text-red-600 truncate">
                                    {config.last_sync_error}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Schedule */}
                            <div className="w-[180px] flex items-center gap-1">
                              <SchedulePicker
                                currentSpec={config.schedule_spec}
                                onSave={(spec) =>
                                  handleSaveSchedule(config.id, spec)
                                }
                              >
                                <button
                                  type="button"
                                  title={
                                    scheduled ? schedule : 'Set schedule'
                                  }
                                  className={`flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 transition-colors cursor-pointer hover:ring-2 hover:ring-blue-200 ${
                                    scheduled
                                      ? 'bg-blue-50 text-blue-700'
                                      : 'bg-gray-100 text-gray-500'
                                  }`}
                                >
                                  <Clock className="h-3 w-3" />
                                  <span className="truncate">
                                    {scheduled ? schedule : 'Set schedule'}
                                  </span>
                                </button>
                              </SchedulePicker>
                            </div>

                            {/* Toggle */}
                            <div className="w-[80px] flex items-center justify-center gap-1.5">
                              <Switch
                                checked={config.enabled}
                                onChange={(checked: boolean) =>
                                  handleToggleEnabled(config.id, checked)
                                }
                                color={config.enabled ? 'blue' : undefined}
                              />
                              <span
                                className={`text-xs font-medium w-6 ${config.enabled ? 'text-blue-600' : 'text-gray-500'}`}
                              >
                                {config.enabled ? 'On' : 'Off'}
                              </span>
                            </div>

                            {/* Actions */}
                            <div className="w-[120px] flex items-center justify-end gap-0.5">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={`h-7 w-7 transition-opacity ${
                                      config.last_sync_error ||
                                      expandedHistoryConfigId === config.id
                                        ? 'text-amber-600 opacity-100'
                                        : 'text-gray-500 hover:text-indigo-500 opacity-0 group-hover:opacity-100'
                                    }`}
                                    onClick={() => toggleHistory(config.id)}
                                  >
                                    <Activity className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Run details</TooltipContent>
                              </Tooltip>
                              {config.enabled && scheduled && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-green-500 hover:text-green-600 transition-colors"
                                      disabled={isTriggering}
                                      onClick={() =>
                                        handleTrigger(config.id)
                                      }
                                    >
                                      <PlayCircle className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Trigger now</TooltipContent>
                                </Tooltip>
                              )}
                              {config.temporal_schedule_id && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <a
                                      href={buildTemporalScheduleUrl(
                                        config.temporal_schedule_id,
                                      )}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:text-blue-500 transition-colors"
                                    >
                                      <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    View in Temporal
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-gray-500 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => startEditingNote(config)}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit note</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-gray-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleDelete(config.id)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Remove</TooltipContent>
                              </Tooltip>
                            </div>
                          </div>

                          {expandedHistoryConfigId === config.id && (
                            <RunHistoryPanel
                              config={config}
                              runs={detailsData?.runs ?? []}
                              isLoading={isFetchingDetails}
                              onRefresh={() => refetchDetails()}
                            />
                          )}

                          {/* Inline note editor */}
                          {isEditingNote && (
                            <div className="px-5 pb-3 flex items-center gap-2">
                              <Input
                                autoFocus
                                placeholder="Add a note..."
                                value={noteValue}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => setNoteValue(e.target.value)}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                  if (e.key === 'Enter')
                                    handleSaveNote(config.id, noteValue);
                                  if (e.key === 'Escape')
                                    setEditingNoteId(null);
                                }}
                                className="text-xs h-8 flex-1"
                              />
                              <Button
                                size="sm"
                                className="h-8 text-xs px-3"
                                onClick={() =>
                                  handleSaveNote(config.id, noteValue)
                                }
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 text-xs px-2"
                                onClick={() => setEditingNoteId(null)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add feature buttons — footer */}
                {addableFeatures.length > 0 && (
                  <div
                    className={`flex flex-wrap gap-2 px-5 py-3 ${
                      existingConfigs.length > 0
                        ? 'border-t bg-gray-50/50'
                        : ''
                    }`}
                  >
                    {addableFeatures.map((feature) => (
                      <Button
                        key={feature}
                        variant="outline"
                        size="sm"
                        className="text-gray-500 border-dashed hover:border-solid hover:border-blue-300 hover:text-blue-600 transition-all text-xs h-8"
                        onClick={() => handleCreateFeature(cmsType, feature)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {FEATURE_DISPLAY[feature]?.name ?? feature}
                      </Button>
                    ))}
                  </div>
                )}

                {existingConfigs.length === 0 &&
                  addableFeatures.length === 0 && (
                    <div className="border-t bg-gray-50/50 px-5 py-3">
                      <p className="text-xs text-gray-500">
                        No features available for this integration.
                      </p>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Schedule picker popover (renders as child of trigger)
// ---------------------------------------------------------------------------

interface SchedulePickerProps {
  currentSpec: ScheduleSpec | null | undefined;
  onSave: (spec: ScheduleSpec) => void;
  children: React.ReactNode;
}

const SchedulePicker: React.FC<SchedulePickerProps> = ({
  currentSpec,
  onSave,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'presets' | 'times' | 'custom'>('presets');
  const [customCron, setCustomCron] = useState('');
  const [customTimezone, setCustomTimezone] = useState('America/New_York');
  const [pendingPreset, setPendingPreset] = useState<SchedulePreset | null>(
    null,
  );
  const [timesTimezone, setTimesTimezone] = useState('America/New_York');
  const [times, setTimes] = useState<string[]>([]);
  const [draftTime, setDraftTime] = useState('07:00');
  const prevOpenRef = useRef(open);

  const activePresetIndex = useMemo(() => {
    return SCHEDULE_PRESETS.findIndex((p) => specsMatch(currentSpec, p.spec));
  }, [currentSpec]);

  const currentOffset = currentSpec?.intervals?.[0]?.offset ?? null;

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    const tz = currentSpec?.time_zone_name ?? 'America/New_York';
    setCustomTimezone(tz);
    setTimesTimezone(tz);

    if (currentSpec?.intervals && currentSpec.intervals.length > 0) {
      setMode('presets');
      return;
    }

    if (currentSpec?.cron_expressions && currentSpec.cron_expressions.length > 0) {
      setCustomCron(currentSpec.cron_expressions[0] ?? '');
      const normalized = canonicalizeDailyCronExpressions(currentSpec.cron_expressions);
      setTimes(normalized.ok ? normalized.times : []);
      setMode(normalized.ok ? 'times' : 'custom');
    } else {
      setCustomCron('');
      setTimes([]);
      setMode('presets');
    }
  }, [open, currentSpec]);

  const handleSelectPreset = (preset: SchedulePreset) => {
    if (preset.spec.intervals && preset.spec.intervals.length > 0) {
      setPendingPreset(preset);
    } else {
      onSave(preset.spec);
      setOpen(false);
      setPendingPreset(null);
    }
  };

  const handleSelectOffset = (offsetValue: string | null) => {
    if (!pendingPreset?.spec.intervals?.[0]) return;
    const spec: ScheduleSpec = {
      intervals: [
        {
          every: pendingPreset.spec.intervals[0].every,
          ...(offsetValue ? { offset: offsetValue } : {}),
        },
      ],
    };
    onSave(spec);
    setOpen(false);
    setPendingPreset(null);
  };

  const handleSaveCustom = () => {
    if (!customCron.trim()) {
      toast.error('Please enter a cron expression');
      return;
    }
    onSave({
      cron_expressions: [customCron.trim()],
      time_zone_name: customTimezone,
    });
    setOpen(false);
    setMode('presets');
    setPendingPreset(null);
  };

  const addTime = (timeStr: string) => {
    const match = timeStr.match(/^(\d{2}):(\d{2})$/);
    if (!match) {
      toast.error('Please choose a valid time');
      return;
    }
    const hour24 = parseInt(match[1] ?? '0', 10);
    const minute = parseInt(match[2] ?? '0', 10);
    if (
      Number.isNaN(hour24) ||
      Number.isNaN(minute) ||
      hour24 > 23 ||
      minute > 59
    ) {
      toast.error('Please choose a valid time');
      return;
    }

    const normalized = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    if (!times.includes(normalized) && times.length >= 8) {
      toast.error('Please choose 8 times or fewer');
      return;
    }
    const next = Array.from(new Set([...times, normalized])).sort(
      (a, b) => timeStrToMinutes(a) - timeStrToMinutes(b),
    );
    setTimes(next);
  };

  const removeTime = (timeStr: string) => {
    setTimes(times.filter((t) => t !== timeStr));
  };

  const handleApplyTimes = () => {
    if (times.length === 0) {
      toast.error('Add at least one time');
      return;
    }

    const cron_expressions = times.map((t) => {
      const [hh, mm] = t.split(':');
      const hour24 = parseInt(hh ?? '0', 10);
      const minute = parseInt(mm ?? '0', 10);
      return `${minute} ${hour24} * * *`;
    });

    onSave({
      cron_expressions,
      time_zone_name: timesTimezone,
    });
    setOpen(false);
    setMode('presets');
    setPendingPreset(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setPendingPreset(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        {/* Header */}
        <div className="px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-sm">Sync Schedule</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {pendingPreset
              ? `${pendingPreset.label} — when should syncing start?`
              : mode === 'times'
                ? 'Pick the exact times you want this feature to sync.'
              : 'Choose how often this feature syncs.'}
          </p>
        </div>

        {pendingPreset ? (
          <div>
            <div className="px-4 pt-3 pb-1">
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors"
                onClick={() => setPendingPreset(null)}
              >
                <ChevronDown className="h-3 w-3 rotate-90" />
                Back to frequency
              </button>
            </div>
            <div className="py-1 max-h-[320px] overflow-y-auto">
              {buildOffsetOptions(
                pendingPreset?.spec.intervals?.[0]?.every ?? 'PT24H',
              ).map((opt) => {
                const isActive =
                  (opt.value === null && !currentOffset) ||
                  (opt.value !== null && currentOffset === opt.value);
                return (
                  <button
                    type="button"
                    key={opt.label}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    onClick={() => handleSelectOffset(opt.value)}
                  >
                    <div
                      className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isActive ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`}
                    >
                      {isActive && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {opt.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {opt.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            <div className="flex border-b">
              <button
                type="button"
                className={`flex-1 px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${mode === 'presets' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setMode('presets')}
              >
                Presets
              </button>
              <button
                type="button"
                className={`flex-1 px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${mode === 'times' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setMode('times')}
              >
                Specific times
              </button>
              <button
                type="button"
                className={`flex-1 px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${mode === 'custom' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setMode('custom')}
              >
                Custom Cron
              </button>
            </div>

            {mode === 'presets' ? (
              <div className="py-1 max-h-[320px] overflow-y-auto">
                {SCHEDULE_PRESETS.map((preset, i) => {
                  const isActive = i === activePresetIndex;
                  const isInterval = !!(
                    preset.spec.intervals && preset.spec.intervals.length > 0
                  );
                  return (
                    <button
                      type="button"
                      key={i}
                      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => handleSelectPreset(preset)}
                    >
                      <div
                        className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isActive ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`}
                      >
                        {isActive && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          {preset.label}
                        </div>
                        <div className="text-xs text-gray-500">
                          {preset.description}
                          {isInterval && (
                            <span className="text-gray-500">
                              {' '}
                              — set a start time next
                            </span>
                          )}
                        </div>
                      </div>
                      {isActive && (
                        <Badge
                          variant="default"
                          className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs shrink-0"
                        >
                          Active
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : mode === 'times' ? (
              <div className="px-4 py-3 space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-600">Timezone</Label>
                  <select
                    value={timesTimezone}
                    onChange={(e) => setTimesTimezone(e.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="America/New_York">Eastern (EST/EDT)</option>
                    <option value="America/Chicago">Central (CST/CDT)</option>
                    <option value="America/Denver">Mountain (MST/MDT)</option>
                    <option value="America/Los_Angeles">Pacific (PST/PDT)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-600">Times</Label>
                    <span className="text-[11px] text-gray-500">
                      {times.length} selected
                    </span>
                  </div>

                  {times.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {times.map((t) => (
                        <div
                          key={t}
                          className="group inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium">
                            {(() => {
                              const [hh, mm] = t.split(':');
                              return formatTimeOfDay(
                                parseInt(hh ?? '0', 10),
                                parseInt(mm ?? '0', 10),
                              );
                            })()}
                          </span>
                          <button
                            type="button"
                            className="ml-0.5 rounded-full p-0.5 text-gray-500 hover:text-gray-600"
                            onClick={() => removeTime(t)}
                            aria-label={`Remove ${t}`}
                            title="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                      Add one or more times below.
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={draftTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setDraftTime(e.target.value)
                      }
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') addTime(draftTime);
                      }}
                      className="text-sm"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => addTime(draftTime)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    {[
                      { label: '7:00 AM', value: '07:00' },
                      { label: '12:00 PM', value: '12:00' },
                      { label: '4:00 PM', value: '16:00' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className="text-[11px] rounded-full bg-blue-50 text-blue-700 px-2 py-1 hover:bg-blue-100 transition-colors"
                        onClick={() => addTime(opt.value)}
                      >
                        + {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="text-xs text-gray-500 pt-1">
                    Preview:{' '}
                    <span className="font-medium text-gray-700">
                      {describeTimes(times, timesTimezone)}
                    </span>
                  </div>
                </div>

                <div className="pt-1 flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="flex-1"
                    onClick={handleApplyTimes}
                    disabled={times.length === 0}
                  >
                    Apply Times
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-gray-500"
                    onClick={() => setTimes([])}
                    disabled={times.length === 0}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            ) : mode === 'custom' ? (
              <div className="p-4 space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-600">
                    Cron Expression
                  </Label>
                  <Input
                    placeholder="0 7,12 * * *"
                    value={customCron}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCustomCron(e.target.value)
                    }
                    className="text-sm font-mono"
                  />
                  <p className="text-xs text-gray-500">
                    Format: minute hour day-of-month month day-of-week
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-600">Timezone</Label>
                  <select
                    value={customTimezone}
                    onChange={(e) => setCustomTimezone(e.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="America/New_York">Eastern (EST/EDT)</option>
                    <option value="America/Chicago">Central (CST/CDT)</option>
                    <option value="America/Denver">Mountain (MST/MDT)</option>
                    <option value="America/Los_Angeles">
                      Pacific (PST/PDT)
                    </option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
                <Button size="sm" className="w-full" onClick={handleSaveCustom}>
                  Apply Custom Schedule
                </Button>
              </div>
            ) : (
              <div className="p-4">
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                  Choose one or more times in the Specific times tab.
                </div>
              </div>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ---------------------------------------------------------------------------
// Run history panel
// ---------------------------------------------------------------------------

interface RunHistoryPanelProps {
  config: CmsFeatureConfigSchema;
  runs: CmsFeatureSyncRunStatusSchema[];
  isLoading: boolean;
  onRefresh: () => void;
}

const RunHistoryPanel: React.FC<RunHistoryPanelProps> = ({
  config,
  runs,
  isLoading,
  onRefresh,
}) => {
  let historyBody: React.ReactNode;

  if (isLoading) {
    historyBody = (
      <div className="px-4 pb-4">
        <div className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-white/70 px-3 py-2 text-xs text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" />
          Loading run history...
        </div>
      </div>
    );
  } else if (runs.length === 0) {
    historyBody = (
      <div className="px-4 pb-4">
        <div className="rounded-lg border border-dashed border-slate-200 bg-white/70 px-3 py-2 text-xs text-gray-500">
          No Temporal runs found yet.
        </div>
      </div>
    );
  } else {
    historyBody = (
      <div className="space-y-2 px-3 pb-3">
        {runs.map((run) => {
          const busStatusLabel = normalizeWorkflowStatus(run.bus_status);
          const failedMatters = run.matter_failures ?? [];
          const startedAt = run.bus_started_at;

          return (
            <div
              key={run.bus_run_id}
              className="rounded-lg border border-white/80 bg-white/85 px-3 py-2 shadow-[0_8px_20px_-22px_rgba(15,23,42,0.8)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge
                      className={`text-[10px] py-0 h-5 ${statusToneClass(run.bus_status)}`}
                    >
                      {busStatusLabel}
                    </Badge>
                    {run.feature_failed && (
                      <Badge className="text-[10px] py-0 h-5 bg-rose-50 text-rose-700 border border-rose-200">
                        Feature failed
                      </Badge>
                    )}
                    {run.feature_completed && !run.feature_failed && (
                      <Badge className="text-[10px] py-0 h-5 bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Feature completed
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {startedAt
                      ? `${formatRelativeTime(startedAt)} \u2022 ${new Date(startedAt).toLocaleString()}`
                      : 'Start time unavailable'}
                  </p>
                </div>

                {run.child_workflow_id && run.child_run_id && (
                  <a
                    href={buildTemporalWorkflowUrl(
                      run.child_workflow_id,
                      run.child_run_id,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-gray-500 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
                  >
                    Timeline
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>

              {run.feature_error && (
                <p className="mt-2 rounded-md border border-rose-100 bg-rose-50/70 px-2 py-1.5 text-[11px] text-rose-700">
                  {run.feature_error}
                </p>
              )}

              {failedMatters.length > 0 && (
                <details className="mt-2 rounded-md border border-amber-100 bg-amber-50/60 px-2 py-1.5">
                  <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[11px] font-medium text-amber-800">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    {failedMatters.length} failed matter
                    {failedMatters.length === 1 ? '' : 's'}
                  </summary>
                  <div className="mt-1 space-y-1.5">
                    {failedMatters.slice(0, 6).map((failure) => (
                      <div
                        key={`${run.bus_run_id}-${failure.matter_id}`}
                        className="rounded-md border border-amber-100/80 bg-white/80 px-2 py-1"
                      >
                        <p className="font-mono text-[10px] text-gray-500">
                          {failure.matter_id}
                        </p>
                        <p className="text-[11px] text-amber-900/90">
                          {failure.error}
                        </p>
                      </div>
                    ))}
                    {failedMatters.length > 6 && (
                      <p className="text-[11px] text-amber-800/80">
                        +{failedMatters.length - 6} more failed matters
                      </p>
                    )}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="mx-4 mb-3 rounded-xl border border-indigo-100/80 bg-gradient-to-br from-indigo-50/60 via-white to-slate-50/80 shadow-[0_8px_28px_-18px_rgba(79,70,229,0.45)] backdrop-blur-sm">
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <div className="rounded-lg border border-indigo-200 bg-indigo-100/70 p-1.5 text-indigo-700">
            <Activity className="h-3.5 w-3.5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700/90">
              Sync run details
            </p>
            <p className="text-[11px] text-gray-500">
              {runs.length} recent runs for{' '}
              {FEATURE_DISPLAY[config.feature]?.name ?? config.feature}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-gray-500 hover:text-indigo-700"
          onClick={onRefresh}
          disabled={isLoading}
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`}
          />
        </Button>
      </div>
      {historyBody}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sync status badge
// ---------------------------------------------------------------------------

const SyncStatusBadge: React.FC<{ config: CmsFeatureConfigSchema }> = ({
  config,
}) => {
  if (config.last_sync_error) {
    return (
      <Badge variant="destructive" className="text-[10px] gap-1 py-0 h-5">
        <AlertCircle className="h-3 w-3" />
        Error
      </Badge>
    );
  }

  if (config.last_synced_at) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge
            variant="default"
            className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 text-[10px] gap-1 py-0 h-5 cursor-help"
          >
            <Check className="h-3 w-3" />
            {formatRelativeTime(config.last_synced_at)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            Last synced: {new Date(config.last_synced_at).toLocaleString()}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Badge variant="secondary" className="text-[10px] text-gray-500 py-0 h-5">
      Never synced
    </Badge>
  );
};
